import * as React from "react";
import {
  Button,
  Box,
  Chip,
  Grid,
  IconButton,
  Link,
  Table,
  Typography,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  alpha,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Talent } from "../../types/users";
import { useNavigate } from "react-router-dom";
import { CardBase } from "../../components/global";
import dayjs from "dayjs";
import { FilePresent } from "@mui/icons-material";
import { UserDocument } from "../../services/document.service";
import { useUILabel } from "../../hooks/useUILabel";

const TalentProfilePanel = ({
  data,
  documents,
  isLoadingDocuments,
  getDocumentTypeLabel,
}: {
  data?: Talent;
  documents?: UserDocument[];
  isLoadingDocuments?: boolean;
  getDocumentTypeLabel?: (docType?: string) => string;
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const industriesLabel = useUILabel("talent.industries");
  const skillsLabel = useUILabel("talent.skills");
  const noSkillsAddedLabel = useUILabel("talent.noSkillAdded");
  const aboutLabel = useUILabel("talent.about");
  const noBioAvailableLabel = useUILabel("talent.noBioAvailable");
  const workExperienceLabel = useUILabel("talent.workExperience");
  const educationLabel = useUILabel("talent.education");
  const degreeLabel = useUILabel("talent.degree");
  const fieldOfStudyLabel = useUILabel("talent.fieldOfStudy");
  const presentLabel = useUILabel("talent.present");
  const licenseCertificatesLabel = useUILabel("talent.licenseCertificates");
  const numberLabel = useUILabel("talent.number");
  const expiresLabel = useUILabel("talent.expires");
  const specialDocumentsLabel = useUILabel("talent.specialDocuments");
  const untitledDocumentLabel = useUILabel("talent.untitledDocument");
  const viewDocumentLabel = useUILabel("talent.viewDocument");
  const contactLabel = useUILabel("talent.contact");
  const address1Label = useUILabel("talent.address1");
  const address2Label = useUILabel("talent.address2");
  const cityZipLabel = useUILabel("talent.cityZip");
  const countryLabel = useUILabel("talent.country");
  const mondayLabel = useUILabel("talent.monday");
  const tuesdayLabel = useUILabel("talent.tuesday");
  const wednesdayLabel = useUILabel("talent.wednesday");
  const thursdayLabel = useUILabel("talent.thursday");
  const fridayLabel = useUILabel("talent.friday");
  const saturdayLabel = useUILabel("talent.saturday");
  const sundayLabel = useUILabel("talent.sunday");
  const backgroundCheckLabel = useUILabel("talent.backgroundCheck");
  const drugTestLabel = useUILabel("talent.drugTest");
  const validUSCitizenOrHaveWorkVisaLabel = useUILabel("talent.validUSCitizenOrHaveWorkVisa");
  const languagesLabel = useUILabel("talent.languages");
  const noLanguagesAddedLabel = useUILabel("talent.noLanguagesAdded");
  const moreInfoLabel = useUILabel("talent.moreInfo");
  const yesLabel = useUILabel("project.yes");
  const noLabel = useUILabel("project.no");
  const nALabel = useUILabel("talent.nA");
  const stateLabel = useUILabel("common.state");
  const availabilityLabel = useUILabel("talent.availability");

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
      <Grid item lg={8} sm={12} xs={12}>
        <CardBase title={aboutLabel} useGlassCard={true} nested={false}>
          <Typography>{data?.bio || noBioAvailableLabel}</Typography>
        </CardBase>
        <CardBase title={industriesLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          {data?.business_verticals?.length == 0
            ? nALabel
            : data?.business_verticals?.map((it) => (
              <Chip
                label={it.name}
                key={it.id}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: alpha("#12903C", 0.1),
                  color: "#12903C",
                  fontWeight: 600,
                }}
              />
            ))}
        </CardBase>
        <CardBase title={skillsLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          {data?.skills?.length == 0
            ? noSkillsAddedLabel
            : data?.skills?.map((it) => (
              <Chip
                label={it.name}
                key={it.id}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: alpha("#12903C", 0.1),
                  color: "#12903C",
                  fontWeight: 600,
                }}
              />
            ))}
        </CardBase>
        <CardBase title={workExperienceLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={2}>
            {data?.work_experiences?.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 14 }}
                    fontWeight="fontWeightMedium"
                    color="#12903C"
                  >
                    {item?.company || nALabel}
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    <Box sx={{ fontStyle: "italic" }}>
                      {`${dayjs()
                        .set("month", item?.start_period_month - 1)
                        .set("year", item?.start_period_year)
                        .format("MMM YYYY")} to ${item?.end_period_month && item?.end_period_year
                          ? dayjs()
                            .set("month", item?.end_period_month - 1)
                            .set("year", item?.end_period_year)
                            .format("MMM YYYY")
                          : presentLabel
                        }`}
                    </Box>
                    <Box>{item?.description || nALabel}</Box>
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardBase>
        <CardBase title={educationLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={2}>
            {data?.educations?.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 14 }}
                    fontWeight="fontWeightMedium"
                    color="#12903C"
                  >
                    {item?.school}
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    <Box sx={{ fontStyle: "italic" }}>
                      {`${dayjs(item?.start_date).format("MM/DD/YYYY")} to ${item?.end_date
                        ? dayjs(item?.end_date).format("MM/DD/YYYY")
                        : presentLabel
                        }`}
                    </Box>
                  </Typography>
                  <Typography sx={{ fontSize: 14 }} component="div">
                    <Box fontWeight="fontWeightMedium" display="inline">
                      {degreeLabel}
                    </Box>
                    : {item?.degree || nALabel}
                  </Typography>
                  <Typography sx={{ fontSize: 14 }} component="div">
                    <Box fontWeight="fontWeightMedium" display="inline">
                      {fieldOfStudyLabel}
                    </Box>
                    : {item?.field_of_study || nALabel}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardBase>
        <CardBase title={licenseCertificatesLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={2}>
            {data?.certifications?.map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box sx={{ mb: 2 }}>
                  <Link
                    href={item?.certification_url}
                    target="blank"
                    sx={{ color: "#12903C", fontWeight: 600 }}
                  >
                    {item?.certification?.name}
                  </Link>
                  <Typography sx={{ fontSize: 14 }} component="div">
                    <Box fontWeight="fontWeightMedium" display="inline">
                      {numberLabel}
                    </Box>
                    : {`${item?.certification_number || nALabel}`}
                  </Typography>
                  <Typography sx={{ fontSize: 14 }}>
                    <Box fontWeight="fontWeightMedium" display="inline">
                      {expiresLabel}
                    </Box>
                    :{" "}
                    {item?.expiration_date
                      ? dayjs(item?.expiration_date).format("MM/DD/YYYY")
                      : nALabel}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardBase>
        <CardBase title={languagesLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 }, mb: { xs: 0, sm: 0 } }}>
          {data?.languages?.length == 0
            ? noLanguagesAddedLabel
            : data?.languages?.map((it) => (
              <Chip
                label={it.name}
                key={it.id}
                sx={{
                  mr: 1,
                  mb: 1,
                  backgroundColor: alpha("#12903C", 0.1),
                  color: "#12903C",
                  fontWeight: 600,
                }}
              />
            ))}
        </CardBase>
        <CardBase
          title={specialDocumentsLabel}
          useGlassCard={true}
          nested={false}
          sx={{ mt: { xs: 1.5, sm: 2 }, mb: { xs: 0, md: 7 } }}
        >
          {isLoadingDocuments ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : documents && documents.length > 0 ? (
            <List sx={{ py: 0 }}>
              {documents.map((doc) => (
                <ListItem
                  key={doc.id}
                  sx={{
                    px: { xs: 0, sm: 1 },
                    py: 1.5,
                    borderRadius: "8px",
                    mb: 1,
                    "&:hover": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.03)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <FilePresent color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: 15,
                          }}
                        >
                          {doc.name || untitledDocumentLabel}
                        </Typography>
                        {(doc.legal_id_sub_type || doc.document_type) && getDocumentTypeLabel && (
                          <Chip
                            label={getDocumentTypeLabel(
                              doc.legal_id_sub_type || doc.document_type
                            )}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              backgroundColor: alpha("#12903C", 0.1),
                              color: "#12903C",
                            }}
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                        <Link
                          href={doc.url}
                          target="_blank"
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {viewDocumentLabel}
                        </Link>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {dayjs(doc.created_at).format("MMM DD, YYYY")}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">{nALabel}</Typography>
          )}
        </CardBase>
      </Grid>
      <Grid item lg={4} sm={12} xs={12}>
        <CardBase title={contactLabel} useGlassCard={true} nested={false}>
          <Table sx={{ fontSize: 14 }} size="small">
            <TableBody>
              <TableRow>
                <TableCell>{address1Label}</TableCell>
                <TableCell>{data?.physical_address_1 || nALabel}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{address2Label}</TableCell>
                <TableCell>{data?.physical_address_2 || nALabel}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{cityZipLabel}</TableCell>
                <TableCell>
                  {data?.city || nALabel} / {data?.zip || nALabel}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{stateLabel}</TableCell>
                <TableCell>{data?.state || nALabel}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{countryLabel}</TableCell>
                <TableCell>{data?.country?.name || nALabel}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardBase>
        <CardBase title={availabilityLabel} useGlassCard={true} nested={false} sx={{ mt: { xs: 1.5, sm: 2 } }}>
          <Table sx={{ fontSize: 14 }} size="small">
            <TableBody>
              <TableRow>
                <TableCell>{mondayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_monday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{tuesdayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_tuesday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{wednesdayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_wednesday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{thursdayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_thursday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{fridayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_friday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{saturdayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_saturday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{sundayLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.avail_sunday ? "✅" : "❌"}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardBase>
        <CardBase
          title={moreInfoLabel}
          useGlassCard={true}
          nested={false}
          sx={{
            mt: { xs: 1, md: 2 },
            mb: { xs: 7, md: 2 },
            "& .card-base__content": {
              maxHeight: { xs: "300px", sm: "none" },
              overflowY: { xs: "auto", sm: "visible" },
            },
          }}
        >
          <Table sx={{ fontSize: 14 }} size="small">
            <TableBody>
              <TableRow>
                <TableCell>{backgroundCheckLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.allow_background_check ? yesLabel : noLabel}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{drugTestLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.allow_drug_test ? yesLabel : noLabel}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>{validUSCitizenOrHaveWorkVisaLabel}</TableCell>
                <TableCell align="right">
                  {!!data?.talent?.is_valid_us ? yesLabel : noLabel}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardBase>
      </Grid>
    </Grid>
  );
};
export default TalentProfilePanel;
